import { useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { read, utils, type WorkBook } from "xlsx";
import { apiFetch } from "../../utils/api";
import { formatPrice, formatArea } from "../../utils/format";

// ═══════════════════════════════════════════
// 유틸리티 함수
// ═══════════════════════════════════════════

/** 매물분류 컬럼 값 → 시스템 유형 (공백/특수문자 무시) */
function mapPropertyType(raw: unknown): string {
  if (raw === undefined || raw === null) return "";
  // 숫자 등 비문자열 방어
  const s = typeof raw === "string" ? raw : String(raw);
  // 모든 종류의 공백/제어문자 제거 + 유니코드 정규화
  const v = s.replace(/[\s\u00A0\u200B\u200C\u200D\uFEFF\u3000\t\r\n]+/g, "").normalize("NFC");
  if (!v) return "";
  if (v.includes("공장창고")) return "공장";
  if (v.includes("빌딩건물") || v.includes("빌딩") || v.includes("건물")) return "상가";
  if (v.includes("토지")) return "농지";
  if (v.includes("농지")) return "농지";
  if (v.includes("임야")) return "임야";
  if (v.includes("상가") || v.includes("사무실") || v.includes("점포")) return "상가";
  if (v.includes("주택") || v.includes("아파트") || v.includes("빌라")) return "주택";
  if (v.includes("묘터") || v.includes("묘지")) return "묘터";
  if (v.includes("공장")) return "공장";
  if (v.includes("창고")) return "창고";
  return "";
}

/** 매물특징+소재지 텍스트에서 유형 자동 추론 */
function inferPropertyType(description: string, address: string): string {
  const text = `${description} ${address}`.replace(/[\s\u00A0]+/g, "").normalize("NFC");
  if (!text) return "농지";

  // 순서: 구체적인 것 먼저
  if (/공장|창고|물류|제조|산업/.test(text)) return "공장";
  if (/임야|산림|산지/.test(text)) return "임야";
  if (/주택|아파트|빌라|단독|모텔|원룸|다세대|다가구|연립/.test(text)) return "주택";
  if (/상가|빌딩|건물|사무실|점포|오피스|매장/.test(text)) return "상가";
  if (/묘|묘터|묘지|납골|봉안/.test(text)) return "묘터";

  // 위에 해당 안 되면 농지 (토지 매물이 대부분)
  return "농지";
}

/** 주소 파싱 (공백 기준 위치 분리) */
function parseAddress(raw: string) {
  const parts = raw.trim().split(/\s+/);
  return {
    sido: parts[0] || "",
    sigungu: parts[1] || "",
    eupmyeondong: parts[2] || "",
    detail: parts.slice(3).join(" "),
  };
}

/** 면적 파싱 "5,592.00 (㎡)" → 평 변환 */
function parseArea(raw: string | number): number {
  if (typeof raw === "number") return Math.round(raw * 0.3025 * 100) / 100;
  const cleaned = String(raw).replace(/,/g, "").replace(/\(.*\)/, "").trim();
  const sqm = parseFloat(cleaned);
  if (isNaN(sqm)) return 0;
  return Math.round(sqm * 0.3025 * 100) / 100;
}

/** 가격 파싱 "51,000" → 숫자 */
function parsePrice(raw: string | number): number {
  if (typeof raw === "number") return raw;
  return parseFloat(String(raw).replace(/,/g, "").trim()) || 0;
}

/** 엑셀 컬럼 헤더 → 내부 키 매핑 */
function findColumnKey(header: string): string | null {
  const h = header.replace(/[\s\u00A0]+/g, "").normalize("NFC");
  if (h.includes("매물분류") || h.includes("매물유형") || h === "유형" || h === "종류" || h === "구분") return "property_type";
  if (h.includes("소재지") || h === "주소" || h === "위치") return "address";
  if (h.includes("면적")) return "area";
  if (h.includes("매물가") || h.includes("매매가") || h.includes("가격")) return "price";
  if (h.includes("매물특징") || h.includes("특징")) return "description";
  if (h.includes("관리자메모") || h.includes("비고") || h.includes("비공개")) return "detailed_notes";
  if (h.includes("용도지역")) return "zoning";
  if (h.includes("도로") || h.includes("접도")) return "road_access";
  if (h.includes("수도") || h.includes("용수")) return "water_supply";
  if (h.includes("추천용도") || h.includes("추천")) return "recommended_use";
  if (h.includes("연락처") || h.includes("소유주")) return "owner_contact";
  return null;
}

// ═══════════════════════════════════════════
// 카카오 지오코딩
// ═══════════════════════════════════════════

declare global {
  interface Window { kakao: any; __kakaoMapLoading?: boolean; }
}

const KAKAO_KEY = import.meta.env.VITE_KAKAO_MAP_KEY;

function ensureKakaoSDK(): Promise<boolean> {
  return new Promise((resolve) => {
    if (!KAKAO_KEY) {
      resolve(false);
      return;
    }

    // 이미 로드됨
    if (window.kakao?.maps?.services) {
      resolve(true);
      return;
    }

    // 다른 곳에서 로딩 중 → 대기 (최대 15초)
    if (window.__kakaoMapLoading) {
      let waited = 0;
      const id = setInterval(() => {
        waited += 200;
        if (window.kakao?.maps?.services) {
          clearInterval(id);
          resolve(true);
        } else if (waited >= 15000) {
          clearInterval(id);
          resolve(false);
        }
      }, 200);
      return;
    }

    // 직접 로딩
    window.__kakaoMapLoading = true;
    const script = document.createElement("script");
    script.src = `https://dapi.kakao.com/v2/maps/sdk.js?appkey=${KAKAO_KEY}&autoload=false&libraries=services`;

    // 타임아웃 (15초)
    const timeout = setTimeout(() => {
      window.__kakaoMapLoading = false;
      resolve(false);
    }, 15000);

    script.onload = () => {
      if (!window.kakao?.maps?.load) {
        clearTimeout(timeout);
        window.__kakaoMapLoading = false;
        resolve(false);
        return;
      }
      window.kakao.maps.load(() => {
        clearTimeout(timeout);
        window.__kakaoMapLoading = false;
        resolve(!!window.kakao?.maps?.services);
      });
    };

    script.onerror = () => {
      clearTimeout(timeout);
      window.__kakaoMapLoading = false;
      resolve(false);
    };

    document.head.appendChild(script);
  });
}

function geocodeAddress(address: string): Promise<{ lat: number; lng: number } | null> {
  return new Promise((resolve) => {
    if (!window.kakao?.maps?.services) {
      resolve(null);
      return;
    }
    const gc = new window.kakao.maps.services.Geocoder();
    gc.addressSearch(address, (r: any[], status: any) => {
      if (status === window.kakao.maps.services.Status.OK && r[0]) {
        resolve({ lat: parseFloat(r[0].y), lng: parseFloat(r[0].x) });
      } else {
        resolve(null);
      }
    });
  });
}

function geocodeRegion(sido: string, sigungu: string, emd: string): Promise<{ lat: number; lng: number } | null> {
  return new Promise((resolve) => {
    if (!window.kakao?.maps?.services) {
      resolve(null);
      return;
    }
    const gc = new window.kakao.maps.services.Geocoder();
    const query = `${sido} ${sigungu} ${emd}`.trim();
    gc.addressSearch(query, (r: any[], status: any) => {
      if (status === window.kakao.maps.services.Status.OK && r[0]) {
        resolve({ lat: Math.round(parseFloat(r[0].y) * 10000) / 10000, lng: Math.round(parseFloat(r[0].x) * 10000) / 10000 });
      } else {
        // 시군구 폴백
        const fallbackQuery = `${sido} ${sigungu}`.trim();
        gc.addressSearch(fallbackQuery, (r2: any[], s2: any) => {
          if (s2 === window.kakao.maps.services.Status.OK && r2[0]) {
            resolve({ lat: Math.round(parseFloat(r2[0].y) * 10000) / 10000, lng: Math.round(parseFloat(r2[0].x) * 10000) / 10000 });
          } else {
            resolve(null);
          }
        });
      }
    });
  });
}

// ═══════════════════════════════════════════
// 타입
// ═══════════════════════════════════════════

interface ParsedRow {
  rowNum: number;
  property_type: string;
  typeInferred: boolean; // 자동 추론된 유형인지
  region_sido: string;
  region_sigungu: string;
  region_eupmyeondong: string;
  exact_address: string;
  area_pyeong: number;
  price_manwon: number;
  description: string;
  detailed_notes: string;
  exact_lat: number;
  exact_lng: number;
  blind_lat: number;
  blind_lng: number;
  geocodeStatus: "pending" | "done" | "fallback";
}

interface BulkResult {
  total: number;
  success: number;
  failed: number;
  results: { index: number; id?: string; error?: string }[];
}

type ProgressPhase = "idle" | "geocoding" | "registering" | "done";

// ═══════════════════════════════════════════
// 메인 컴포넌트
// ═══════════════════════════════════════════

export function BulkUploadPage() {
  const navigate = useNavigate();
  const fileRef = useRef<HTMLInputElement>(null);

  const [rows, setRows] = useState<ParsedRow[]>([]);
  const [step, setStep] = useState<"upload" | "preview" | "processing" | "done">("upload");
  const [error, setError] = useState("");
  const [phase, setPhase] = useState<ProgressPhase>("idle");
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [bulkResult, setBulkResult] = useState<BulkResult | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [fileName, setFileName] = useState("");

  // ── 엑셀 파일 처리 ────────────────────────
  const processFile = useCallback((file: File) => {
    if (!/\.(xlsx|xls)$/i.test(file.name)) {
      setError("엑셀 파일(.xlsx, .xls)만 업로드 가능합니다");
      return;
    }
    setError("");
    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target!.result as ArrayBuffer);
        const wb: WorkBook = read(data, { type: "array" });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const json = utils.sheet_to_json<Record<string, any>>(sheet, { defval: "" });

        if (json.length === 0) { setError("엑셀에 데이터가 없습니다"); return; }

        // 헤더 매핑
        const rawHeaders = Object.keys(json[0]);
        const colMap: Record<string, string> = {};
        for (const h of rawHeaders) {
          const key = findColumnKey(h);
          if (key) colMap[key] = h;
        }

        if (!colMap.address) {
          setError("'소재지' 또는 '주소' 컬럼을 찾을 수 없습니다. 엑셀 컬럼: " + rawHeaders.join(", "));
          return;
        }

        const hasTypeCol = !!colMap.property_type;

        const parsed: ParsedRow[] = json.map((row, idx) => {
          const addr = parseAddress(String(row[colMap.address] || ""));
          const desc = String(row[colMap.description] || "");
          const detailedNotes = String(row[colMap.detailed_notes] || "");
          const areaPyeong = colMap.area ? parseArea(row[colMap.area]) : 0;
          const priceManwon = colMap.price ? parsePrice(row[colMap.price]) : 0;

          // 유형 결정: 1) 매물분류 컬럼값 → 2) 자동추론
          const rawTypeValue = hasTypeCol ? row[colMap.property_type] : undefined;
          let propertyType = "";
          let typeInferred = false;

          if (hasTypeCol && rawTypeValue !== undefined && rawTypeValue !== null && rawTypeValue !== "") {
            propertyType = mapPropertyType(rawTypeValue);
          }

          if (!propertyType) {
            propertyType = inferPropertyType(desc, String(row[colMap.address] || ""));
            typeInferred = true;
          }

          // 최종 안전장치: 빈 값이면 무조건 "농지"
          if (!propertyType) {
            propertyType = "농지";
            typeInferred = true;
          }

          return {
            rowNum: idx + 2,
            property_type: propertyType,
            typeInferred,
            region_sido: addr.sido,
            region_sigungu: addr.sigungu,
            region_eupmyeondong: addr.eupmyeondong,
            exact_address: String(row[colMap.address] || ""),
            area_pyeong: areaPyeong,
            price_manwon: priceManwon,
            description: desc,
            detailed_notes: detailedNotes,
            exact_lat: 0,
            exact_lng: 0,
            blind_lat: 0,
            blind_lng: 0,
            geocodeStatus: "pending" as const,
          };
        });

        setRows(parsed);
        setStep("preview");
      } catch (err: any) {
        setError("엑셀 파싱 실패: " + (err.message || "알 수 없는 오류"));
      }
    };
    reader.readAsArrayBuffer(file);
  }, []);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  }, [processFile]);

  const handleDragOver = useCallback((e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); }, []);
  const handleDragLeave = useCallback((e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); }, []);
  const handleDrop = useCallback((e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); const f = e.dataTransfer.files?.[0]; if (f) processFile(f); }, [processFile]);

  const updateRow = (idx: number, key: keyof ParsedRow, value: any) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, [key]: value, ...(key === "property_type" ? { typeInferred: false } : {}) } : r)));
  };

  const removeRow = (idx: number) => {
    setRows((prev) => prev.filter((_, i) => i !== idx));
  };

  // ── 일괄 등록 (좌표변환 → 등록 통합) ──────────
  const handleBulkRegister = async () => {
    if (rows.length === 0) return;
    setError("");
    setStep("processing");

    // Phase 1: 좌표 변환
    setPhase("geocoding");
    const total = rows.length;
    setProgress({ done: 0, total });

    const sdkOk = await ensureKakaoSDK();

    if (!sdkOk) {
      setError("카카오 지도 SDK 로딩 실패 — 좌표 없이 등록됩니다. (나중에 좌표를 추가할 수 있습니다)");
    }

    const updated = [...rows];

    for (let i = 0; i < updated.length; i++) {
      const row = updated[i];

      if (sdkOk) {
        // exact 좌표 (전체 주소)
        const exact = await geocodeAddress(row.exact_address);
        if (exact) {
          row.exact_lat = exact.lat;
          row.exact_lng = exact.lng;
        }

        // blind 좌표 (읍면동/시군구 중심)
        const blind = await geocodeRegion(row.region_sido, row.region_sigungu, row.region_eupmyeondong);
        if (blind) {
          row.blind_lat = blind.lat;
          row.blind_lng = blind.lng;
        }

        // exact 실패 → blind 좌표로 대체
        if (!exact && blind) {
          row.exact_lat = blind.lat;
          row.exact_lng = blind.lng;
          row.geocodeStatus = "fallback";
        } else if (exact && blind) {
          row.geocodeStatus = "done";
        } else if (exact && !blind) {
          // blind 실패 → exact 좌표를 소수점 4자리로 사용
          row.blind_lat = Math.round(exact.lat * 10000) / 10000;
          row.blind_lng = Math.round(exact.lng * 10000) / 10000;
          row.geocodeStatus = "fallback";
        } else {
          row.geocodeStatus = "fallback";
        }
      } else {
        // SDK 없으면 좌표 0,0으로 진행
        row.geocodeStatus = "fallback";
      }

      setProgress({ done: i + 1, total });
    }

    setRows([...updated]);

    // Phase 2: 일괄 등록 (50건씩 배치)
    setPhase("registering");

    // 유효한 건만 필터 (면적 또는 가격이 0이어도 등록 허용 — 주소만 있으면 OK)
    const toRegister = updated.filter(
      (r) => r.region_sido && r.region_sigungu,
    );

    if (toRegister.length === 0) {
      setError("등록 가능한 매물이 없습니다.");
      setStep("preview");
      setPhase("idle");
      return;
    }

    const payload = toRegister.map((r) => ({
      property_type: r.property_type,
      region_sido: r.region_sido,
      region_sigungu: r.region_sigungu,
      region_eupmyeondong: r.region_eupmyeondong,
      exact_address: r.exact_address,
      area_pyeong: r.area_pyeong,
      price_manwon: r.price_manwon,
      exact_lat: r.exact_lat,
      exact_lng: r.exact_lng,
      blind_lat: r.blind_lat,
      blind_lng: r.blind_lng,
      description: r.description,
      detailed_notes: r.detailed_notes,
    }));

    const BATCH_SIZE = 20;
    const batches: typeof payload[] = [];
    for (let i = 0; i < payload.length; i += BATCH_SIZE) {
      batches.push(payload.slice(i, i + BATCH_SIZE));
    }

    setProgress({ done: 0, total: payload.length });

    try {
      const accumulated: BulkResult = { total: payload.length, success: 0, failed: 0, results: [] };
      let offset = 0;

      for (let bi = 0; bi < batches.length; bi++) {
        const batch = batches[bi];
        const res = await apiFetch<BulkResult>("/api/admin/listings/bulk", {
          method: "POST",
          body: JSON.stringify({ listings: batch }),
        });
        accumulated.success += res.success;
        accumulated.failed += res.failed;
        accumulated.results.push(...res.results.map((r) => ({ ...r, index: r.index + offset })));
        offset += batch.length;
        setProgress({ done: offset, total: payload.length });

        // 3초 딜레이 (마지막 배치 제외)
        if (bi < batches.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 3000));
        }
      }

      setBulkResult(accumulated);
      setPhase("done");
      setStep("done");
    } catch (err: any) {
      setError(err.message || "일괄 등록 실패");
      setStep("preview");
      setPhase("idle");
    }
  };

  // ═══════════════════════════════════════════
  // 렌더링
  // ═══════════════════════════════════════════

  // ── 완료 화면 ────────────────────────────
  if (step === "done" && bulkResult) {
    return (
      <div style={st.page}>
        <div style={st.container}>
          <div style={st.centerCard}>
            <span style={st.doneIcon}>{"✓"}</span>
            <h2 style={st.centerTitle}>일괄 등록 완료</h2>
            <div style={st.doneStats}>
              <p>
                전체 <strong>{bulkResult.total}</strong>건 중{" "}
                <span style={{ color: "var(--lt-green)" }}>
                  <strong>{bulkResult.success}</strong>건 성공
                </span>
                {bulkResult.failed > 0 && (
                  <span style={{ color: "var(--lt-red)" }}>
                    , <strong>{bulkResult.failed}</strong>건 실패
                  </span>
                )}
              </p>
            </div>
            <div style={st.centerActions}>
              <button onClick={() => navigate("/admin")} style={st.primaryBtn}>
                관리자 페이지로
              </button>
              <button
                onClick={() => {
                  setRows([]); setStep("upload"); setBulkResult(null);
                  setPhase("idle"); setFileName("");
                  if (fileRef.current) fileRef.current.value = "";
                }}
                style={st.secondaryBtn}
              >
                추가 업로드
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── 진행 중 화면 (좌표변환 + 등록 통합) ────────
  if (step === "processing") {
    const pct = progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;
    const phaseLabel = phase === "geocoding"
      ? (progress.done > 0 ? `좌표 변환 중 (${progress.done}/${progress.total})...` : `카카오 지도 SDK 로딩 중...`)
      : `등록 중 (${progress.done}/${progress.total})...`;

    return (
      <div style={st.page}>
        <div style={st.container}>
          <div style={st.centerCard}>
            <h2 style={{ ...st.centerTitle, marginBottom: 16 }}>{phaseLabel}</h2>
            <div style={st.progressBar}>
              <div style={{ ...st.progressFill, width: `${pct}%` }} />
            </div>
            <p style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 12 }}>
              {phase === "geocoding" && (progress.done > 0
                ? "카카오 Geocoder로 주소를 좌표로 변환하고 있습니다"
                : "SDK를 불러오는 중입니다. 잠시만 기다려주세요..."
              )}
              {phase === "registering" && "Firestore에 매물을 저장하고 있습니다"}
            </p>
            {error && <p style={{ ...st.error, marginTop: 12 }}>{error}</p>}
          </div>
        </div>
      </div>
    );
  }

  // ── 업로드 / 미리보기 화면 ────────────────────
  const inferredCount = rows.filter((r) => r.typeInferred).length;

  return (
    <div style={st.page}>
      <div style={st.container}>
        <header style={st.header}>
          <button onClick={() => navigate("/admin")} style={st.backBtn}>
            {"< 관리자"}
          </button>
          <h1 style={st.title}>엑셀 매물 일괄 등록</h1>
          <div style={{ width: 60 }} />
        </header>

        {/* 드롭존 */}
        <div
          style={{
            ...st.dropzone,
            borderColor: dragOver ? "var(--lt-gold)" : "var(--border-medium)",
            background: dragOver ? "rgba(212,168,83,0.08)" : "var(--bg-card)",
          }}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileRef.current?.click()}
        >
          <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={handleFileInput} style={{ display: "none" }} />
          <div style={st.dropIcon}>{dragOver ? "\uD83D\uDCE5" : "\uD83D\uDCC4"}</div>
          {fileName
            ? <p style={st.dropFileName}>{fileName}</p>
            : <p style={st.dropText}>엑셀 파일을 여기에 끌어다 놓으세요</p>
          }
          <p style={st.dropSub}>또는 클릭하여 파일 선택 (.xlsx, .xls)</p>
          <p style={st.uploadHint}>
            필수: 소재지, 면적, 매물가(만원) | 선택: 매물분류, 매물특징, 관리자메모
          </p>
        </div>

        {error && <p style={st.error}>{error}</p>}

        {/* 미리보기 테이블 */}
        {rows.length > 0 && (
          <>
            <div style={st.tableInfo}>
              <span>총 <strong>{rows.length}</strong>건</span>
              {inferredCount > 0 && (
                <span style={{ color: "var(--lt-orange)" }}>
                  {" | 유형 자동추론 "}<strong>{inferredCount}</strong>건
                </span>
              )}
            </div>

            <div style={st.tableWrap}>
              <table style={st.table}>
                <thead>
                  <tr>
                    <th style={st.th}>#</th>
                    <th style={st.th}>유형</th>
                    <th style={st.th}>시도</th>
                    <th style={st.th}>시군구</th>
                    <th style={st.th}>읍면동</th>
                    <th style={st.th}>면적</th>
                    <th style={st.th}>가격</th>
                    <th style={st.th}></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, idx) => (
                    <tr key={idx} style={idx % 2 === 0 ? st.trEven : undefined}>
                      <td style={st.td}>{row.rowNum}</td>
                      <td style={st.td}>
                        <select
                          value={row.property_type}
                          onChange={(e) => updateRow(idx, "property_type", e.target.value)}
                          style={{
                            ...st.miniSelect,
                            ...(row.typeInferred ? { borderColor: "var(--lt-orange)", color: "var(--lt-orange)" } : {}),
                          }}
                          title={row.typeInferred ? "자동 추론됨 (수정 가능)" : ""}
                        >
                          <option value="">- 선택 -</option>
                          {["농지", "임야", "상가", "주택", "묘터", "공장", "창고"].map((t) => (
                            <option key={t} value={t}>{t}</option>
                          ))}
                        </select>
                      </td>
                      <td style={st.td}>{row.region_sido}</td>
                      <td style={st.td}>{row.region_sigungu}</td>
                      <td style={st.td}>{row.region_eupmyeondong}</td>
                      <td style={st.td}>{row.area_pyeong > 0 ? formatArea(row.area_pyeong) : "-"}</td>
                      <td style={st.td}>{row.price_manwon > 0 ? formatPrice(row.price_manwon) : "-"}</td>
                      <td style={st.td}>
                        <button onClick={() => removeRow(idx)} style={st.deleteBtn} title="삭제">{"x"}</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* 일괄 등록 버튼 (하나로 통합) */}
            <div style={st.actions}>
              <button onClick={handleBulkRegister} style={st.primaryBtn}>
                일괄 등록 ({rows.length}건)
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════
// 스타일
// ═══════════════════════════════════════════

const st: Record<string, React.CSSProperties> = {
  page: { minHeight: "100vh", background: "var(--bg-primary)" },
  container: { maxWidth: 1200, margin: "0 auto", padding: "0 32px 40px" },
  header: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "16px 0", borderBottom: "1px solid var(--border-light)", marginBottom: 20,
  },
  backBtn: {
    color: "var(--text-muted)", fontSize: 14, fontWeight: 500,
    background: "none", border: "none", cursor: "pointer",
  },
  title: { fontSize: 18, fontWeight: 700, color: "var(--text-primary)", margin: 0 },
  dropzone: {
    textAlign: "center" as const, padding: "40px 32px",
    border: "2px dashed var(--border-medium)", borderRadius: "var(--radius-xl)",
    marginBottom: 16, cursor: "pointer", transition: "border-color 0.2s, background 0.2s",
  },
  dropIcon: { fontSize: 48, marginBottom: 12, lineHeight: 1 },
  dropText: { fontSize: 16, fontWeight: 600, color: "var(--text-primary)", margin: "0 0 6px" },
  dropFileName: { fontSize: 15, fontWeight: 600, color: "var(--lt-gold)", margin: "0 0 6px" },
  dropSub: { fontSize: 13, color: "var(--text-muted)", margin: "0 0 12px" },
  uploadHint: {
    fontSize: 12, color: "var(--text-muted)", margin: 0,
    padding: "8px 12px", background: "var(--bg-secondary)",
    borderRadius: "var(--radius-sm)", display: "inline-block",
  },
  error: {
    color: "var(--lt-red)", fontSize: 14, padding: "10px 16px",
    background: "rgba(239,68,68,0.1)", borderRadius: "var(--radius-md)", margin: "0 0 12px",
  },
  tableInfo: { fontSize: 14, color: "var(--text-secondary)", marginBottom: 8 },
  tableWrap: {
    overflowX: "auto" as const, border: "1px solid var(--border-light)",
    borderRadius: "var(--radius-lg)", background: "var(--bg-card)",
  },
  table: { width: "100%", borderCollapse: "collapse" as const, fontSize: 13 },
  th: {
    textAlign: "left" as const, padding: "10px 10px", fontWeight: 600,
    color: "var(--text-muted)", borderBottom: "1px solid var(--border-light)",
    whiteSpace: "nowrap" as const, fontSize: 12,
  },
  td: {
    padding: "8px 10px", borderBottom: "1px solid var(--border-light)",
    whiteSpace: "nowrap" as const, color: "var(--text-primary)",
  },
  trEven: { background: "var(--bg-secondary)" },
  miniSelect: {
    padding: "3px 6px", borderRadius: 4, border: "1px solid var(--border-medium)",
    background: "var(--bg-secondary)", color: "var(--text-primary)", fontSize: 12,
  },
  deleteBtn: {
    padding: "2px 8px", borderRadius: 4, border: "1px solid var(--border-medium)",
    background: "transparent", color: "var(--text-muted)", fontSize: 12, cursor: "pointer",
  },
  actions: { display: "flex", gap: 12, justifyContent: "flex-end", marginTop: 16 },
  primaryBtn: {
    padding: "12px 28px", borderRadius: "var(--radius-md)", border: "none",
    background: "var(--lt-gold)", color: "#0F172A", fontSize: 15, fontWeight: 600, cursor: "pointer",
  },
  secondaryBtn: {
    padding: "12px 20px", borderRadius: "var(--radius-md)",
    border: "1px solid var(--border-medium)", background: "transparent",
    color: "var(--text-secondary)", fontSize: 14, fontWeight: 600, cursor: "pointer",
  },
  centerCard: {
    textAlign: "center" as const, padding: 40,
    background: "var(--bg-card)", border: "1px solid var(--border-light)",
    borderRadius: "var(--radius-xl)", marginTop: 40,
  },
  centerTitle: { fontSize: 20, fontWeight: 700, color: "var(--text-primary)", margin: 0 },
  doneIcon: {
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    width: 56, height: 56, borderRadius: "50%",
    background: "rgba(16,185,129,0.15)", color: "var(--lt-green)",
    fontSize: 28, fontWeight: 700, marginBottom: 16,
  },
  doneStats: { fontSize: 15, color: "var(--text-secondary)", margin: "12px 0 0" },
  centerActions: { display: "flex", gap: 12, justifyContent: "center", marginTop: 24 },
  progressBar: {
    width: "100%", height: 8, borderRadius: 4,
    background: "var(--bg-secondary)", overflow: "hidden" as const,
  },
  progressFill: {
    height: "100%", borderRadius: 4, background: "var(--lt-gold)",
    transition: "width 0.3s ease",
  },
};
